'use client'

import React from 'react'
import { DashboardCard } from './DashboardCard'
import { CHART_MIN_HEIGHT } from './chartTheme'

export interface InfoStat {
  label: string
  value: React.ReactNode
}

export interface ChartFrameProps {
  title?: string
  /** Controls in the header row — a year picker, a series filter. */
  action?: React.ReactNode
  /** The chart itself: a recharts ResponsiveContainer, sized by this frame. */
  children: React.ReactNode
  loading?: boolean
  error?: string | null
  /** Shown instead of the plot when the series has no points. */
  emptyMessage?: string
  isEmpty?: boolean
  /** Insight tiles under the plot — "Best month", "Total for 2026". */
  stats?: InfoStat[]
  legend?: React.ReactNode
  className?: string
}

/**
 * The card a chart lives in.
 *
 * Deliberately does not import recharts: packages/ui does not depend on it, and
 * making it do so to render a wrapper would put a charting library in the
 * dependency tree of every app that touches this package. The caller passes the
 * ResponsiveContainer; this owns the card, the header, the reserved plot height
 * and the loading/error/empty branches, which is where charts previously
 * disagreed with each other.
 *
 * The plot keeps its height in every branch, so a chart that arrives after its
 * neighbours does not shove the page around as it lands.
 */
export const ChartFrame: React.FC<ChartFrameProps> = ({
  title,
  action,
  children,
  loading = false,
  error = null,
  emptyMessage = 'No data for this period.',
  isEmpty = false,
  stats,
  legend,
  className = '',
}) => (
  <DashboardCard title={title} action={action} padded={false} className={className}>
    <div className={`flex-1 ${CHART_MIN_HEIGHT} px-3 pb-2`}>
      {loading ? (
        <div className="h-full w-full rounded-lg bg-gray-50 animate-pulse" aria-hidden />
      ) : error ? (
        <div
          role="alert"
          className="h-full flex items-center justify-center px-6 text-center text-[13px] text-dashboard-negative font-poppins"
        >
          {error}
        </div>
      ) : isEmpty ? (
        <div className="h-full flex items-center justify-center px-6 text-center text-[13px] text-dashboard-muted font-poppins">
          {emptyMessage}
        </div>
      ) : (
        children
      )}
    </div>

    {legend && <div className="px-4 sm:px-6 pb-1">{legend}</div>}

    {stats && stats.length > 0 && !loading && !error && (
      <div className="px-4 sm:px-6 pb-6 flex flex-col sm:flex-row gap-3">
        {stats.map((stat) => (
          <InfoStatCard key={stat.label} stat={stat} />
        ))}
      </div>
    )}
  </DashboardCard>
)

/** Small label/value tile used under charts and in insight columns. */
export const InfoStatCard: React.FC<{ stat: InfoStat; className?: string }> = ({
  stat,
  className = '',
}) => (
  <div
    className={`flex-1 px-5 py-3.5 bg-dashboard-accent-wash rounded-lg flex flex-col gap-1.5 ${className}`}
  >
    <span className="text-xs font-medium text-dashboard-muted font-poppins leading-5">
      {stat.label}
    </span>
    <span className="text-[13px] font-semibold text-dashboard-text font-poppins leading-5">
      {stat.value}
    </span>
  </div>
)

export interface LegendItem {
  label: string
  color: string
}

/** Horizontal dot legend. Wraps, so a four-series legend survives 375px. */
export const ChartLegend: React.FC<{ items: LegendItem[]; className?: string }> = ({
  items,
  className = '',
}) => (
  <div className={`flex flex-wrap items-center gap-4 ${className}`}>
    {items.map((item) => (
      <div key={item.label} className="flex items-center gap-1.5">
        <span
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: item.color }}
          aria-hidden
        />
        <span className="text-xs font-medium text-gray-600 font-poppins">{item.label}</span>
      </div>
    ))}
  </div>
)

/** The tooltip body every chart uses instead of the recharts default. */
export const ChartTooltip: React.FC<{ label?: React.ReactNode; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="bg-white rounded-xl shadow-[var(--dashboard-shadow-tooltip)] px-4 py-3">
    {label && (
      <p className="text-[13px] font-semibold text-dashboard-text font-poppins mb-1">{label}</p>
    )}
    <div className="text-xs text-gray-600 font-poppins">{children}</div>
  </div>
)
